import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, map } from 'rxjs';
import { NeighborhoodInsight } from '../../order/interface/neighborhood.interface';
import { OrderDetail, PropertyMap } from '../../order/interface/order.interface';
import { ExtendedPropertyDetail } from '../../property/interface/property.interface';
import { ScenarioMap } from '../../scenario/interface/scenario.interface';
import { SyncBatch, SyncRevisionMap, SyncScope } from '../interface/sync.interface';
import {
  isSettledThrough,
  requestedRevisions,
  withDispatched,
  withFinished,
  withRequested,
} from '../util/sync-revision.util';

@Injectable({
  providedIn: 'root',
})
export class SyncState {
  constructor() {}

  /** Stores the status of sync in progress */
  private _isSyncInProgressSub$ = new BehaviorSubject<SyncScope[]>([]);
  isSyncInProgress$: Observable<boolean> = this._isSyncInProgressSub$.asObservable().pipe(
    map((scopeList: SyncScope[]): boolean => {
      if (!scopeList.length) return false;
      return true;
    })
  );

  /** Update the current state of the sync for different scopes  */
  setIsSyncInProgress(scope: SyncScope, inProgress: boolean): void {
    const currentScopeSet = new Set(this._isSyncInProgressSub$.value);

    if (inProgress) {
      currentScopeSet.add(scope);
    } else {
      currentScopeSet.delete(scope);
    }

    this._isSyncInProgressSub$.next(Array.from(currentScopeSet));
  }

  /** How far each scope's writes have got, kept in one subject so every change is a single emission. */
  private _revisionsSub$ = new BehaviorSubject<SyncRevisionMap>({});
  revisions$ = this._revisionsSub$.asObservable();

  noteSyncRequested(scope: SyncScope): void {
    this._revisionsSub$.next(withRequested(this._revisionsSub$.value, scope));
  }

  /** The debounce fired and a request is going out; returns the batch to finish when it completes. */
  startSyncBatch(scope: SyncScope): SyncBatch {
    const [revisions, batch] = withDispatched(this._revisionsSub$.value, scope, true);
    this._revisionsSub$.next(revisions);
    return batch;
  }

  /** The debounce fired but there was nothing to send. */
  settleWithoutRequest(scope: SyncScope): void {
    this._revisionsSub$.next(withDispatched(this._revisionsSub$.value, scope, false)[0]);
  }

  finishSyncBatch(scope: SyncScope, batch: SyncBatch): void {
    this._revisionsSub$.next(withFinished(this._revisionsSub$.value, scope, batch));
  }

  /** The latest requested revision of every scope, as the targets a wait must reach. */
  get requestedRevisions(): Partial<Record<SyncScope, number>> {
    return requestedRevisions(this._revisionsSub$.value);
  }

  /** Emits once every write requested up to `targets` has been sent and has finished. */
  settledThrough$(targets: Partial<Record<SyncScope, number>>): Observable<boolean> {
    return this.revisions$.pipe(map((revisions) => isSettledThrough(revisions, targets)));
  }

  /** Stores the last state of the active order when sync was completed successfully */
  private _syncedOrderDetailSub$ = new BehaviorSubject<OrderDetail>({} as OrderDetail);
  syncedOrderDetail$ = this._syncedOrderDetailSub$.asObservable();

  get syncedOrderDetail(): OrderDetail {
    return this._syncedOrderDetailSub$.value;
  }

  set syncedOrderDetail(order: OrderDetail) {
    this._syncedOrderDetailSub$.next(order);
  }

  /** Stores the last state of all the Scenarios when sync was completed successfully */
  private _syncedScenarioMapSub$ = new BehaviorSubject<ScenarioMap | null>(null);
  syncedScenarioMap$ = this._syncedScenarioMapSub$.asObservable();

  get syncedScenarioMap(): ScenarioMap | null {
    return this._syncedScenarioMapSub$.value;
  }

  set syncedScenarioMap(order: ScenarioMap | null) {
    this._syncedScenarioMapSub$.next(order);
  }

  /** Stores the last state of subject property of the active order when sync was completed successfully */
  private _syncedSubjectPropertyDataSub$ = new BehaviorSubject<ExtendedPropertyDetail | null>(null);
  syncedSubjectPropertyData$ = this._syncedSubjectPropertyDataSub$.asObservable();

  get syncedSubjectPropertyData(): ExtendedPropertyDetail | null {
    return this._syncedSubjectPropertyDataSub$.value;
  }

  set syncedSubjectPropertyData(propertyData: ExtendedPropertyDetail | null) {
    this._syncedSubjectPropertyDataSub$.next(propertyData);
  }

  /** Stores the last state of selected comparable properties in an active order when sync was completed successfully */
  private _syncedSelectedComparableMapSub$ = new BehaviorSubject<PropertyMap>({});
  syncedSelectedComparableMap$ = this._syncedSelectedComparableMapSub$.asObservable();

  get syncedSelectedComparableMap(): PropertyMap {
    return this._syncedSelectedComparableMapSub$.value;
  }

  set syncedSelectedComparableMap(order: PropertyMap) {
    this._syncedSelectedComparableMapSub$.next(order);
  }

  /** Stores the last state of neighborhood insights an active order when sync was completed successfully */
  private _syncedNeighborhoodInsightSub$ = new BehaviorSubject<NeighborhoodInsight | null>(null);
  syncedNeighborhoodInsight$ = this._syncedNeighborhoodInsightSub$.asObservable();

  get syncedNeighborhoodInsight(): NeighborhoodInsight | null {
    return this._syncedNeighborhoodInsightSub$.value;
  }

  set syncedNeighborhoodInsight(order: NeighborhoodInsight | null) {
    this._syncedNeighborhoodInsightSub$.next(order);
  }

  /** Stores the last state of selected comparable properties in an active order when sync was completed successfully */
  private _syncedCostApproachSelectedComparableMapSub$ = new BehaviorSubject<PropertyMap>({});
  syncedCostApproachSelectedComparableMap$ = this._syncedCostApproachSelectedComparableMapSub$.asObservable();

  get syncedCostApproachSelectedComparableMap(): PropertyMap {
    return this._syncedCostApproachSelectedComparableMapSub$.value;
  }

  set syncedCostApproachSelectedComparableMap(order: PropertyMap) {
    this._syncedCostApproachSelectedComparableMapSub$.next(order);
  }

  /** Stores the last state of short term rent selected comparable properties in an active order when sync was completed successfully */
  private _syncedShortTermRentSelectedComparableMapSub$ = new BehaviorSubject<PropertyMap>({});
  syncedShortTermRentSelectedComparableMap$ = this._syncedShortTermRentSelectedComparableMapSub$.asObservable();

  get syncedShortTermRentSelectedComparableMap(): PropertyMap {
    return this._syncedShortTermRentSelectedComparableMapSub$.value;
  }

  set syncedShortTermRentSelectedComparableMap(order: PropertyMap) {
    this._syncedShortTermRentSelectedComparableMapSub$.next(order);
  }

  /** Stores the last state of long term rent selected comparable properties in an active order when sync was completed successfully */
  private _syncedLongTermRentSelectedComparableMapSub$ = new BehaviorSubject<PropertyMap>({});
  syncedLongTermRentSelectedComparableMap$ = this._syncedLongTermRentSelectedComparableMapSub$.asObservable();

  get syncedLongTermRentSelectedComparableMap(): PropertyMap {
    return this._syncedLongTermRentSelectedComparableMapSub$.value;
  }

  set syncedLongTermRentSelectedComparableMap(order: PropertyMap) {
    this._syncedLongTermRentSelectedComparableMapSub$.next(order);
  }
}
